import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mail, MessageSquare, Search, Filter, RefreshCw, Send, Loader2, CheckCheck, Check, Trash2 } from 'lucide-react';
import { api, inboxAPI, buyersAPI } from '../services/api';
import toast from '../utils/toast';
import { toastSetupError } from '../components/SetupErrorMessage';
import { format } from 'date-fns';
import EmailReplyItem from '../components/EmailReplyItem';
import ConfirmDialog from '../components/ConfirmDialog';
import './InboxPage.css';

const InboxPage = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [emails, setEmails] = useState([]);
  const [dncMarkedBuyers, setDncMarkedBuyers] = useState({});
  const selectedRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [stats, setStats] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'conversation' | 'message', id }
  const [filters, setFilters] = useState({
    status: 'all',
    channel: 'all',
    search: ''
  });

  useEffect(() => {
    fetchConversations();
    fetchStats();

    // Poll for new messages every 5 minutes
    const pollInterval = setInterval(() => {
      pollNewMessages();
    }, 5 * 60 * 1000);

    return () => clearInterval(pollInterval);
  }, [filters]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.channel !== 'all') params.append('channel', filters.channel);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/inbox/conversations?${params}`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/inbox/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchEmails = async (buyerId) => {
    try {
      const response = await inboxAPI.getEmails(buyerId);
      if (selectedRef.current !== buyerId) return; // stale response
      setEmails(response.data?.emails || []);
    } catch (error) {
      // Email details are optional: never break the conversation view
      console.error('Error fetching emails:', error);
      if (selectedRef.current === buyerId) setEmails([]);
    }
  };

  const handleMarkDnc = async (buyerId) => {
    try {
      const response = await buyersAPI.markDNC([buyerId], 'Unsubscribe request (email reply)');
      if (Number(response?.data?.marked) > 0) {
        toast.success('Marked as Do Not Contact');
        setDncMarkedBuyers((prev) => ({ ...prev, [buyerId]: true }));
        fetchConversations();
        fetchStats();
        return true;
      }
      toast.error('Could not mark Do Not Contact');
      return false;
    } catch (error) {
      console.error('Error marking Do Not Contact:', error);
      toast.error(error.response?.data?.error || 'Failed to mark Do Not Contact');
      return false;
    }
  };

  const fetchMessages = async (buyerId) => {
    try {
      const response = await api.get(`/inbox/conversations/${buyerId}`);
      setMessages(response.data.messages || []);
      if (selectedRef.current !== buyerId) setEmails([]);
      selectedRef.current = buyerId;
      setSelectedConversation(buyerId);
      fetchEmails(buyerId);

      // Refresh conversations to update unread count
      fetchConversations();
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const pollNewMessages = async () => {
    try {
      const lastCheck = localStorage.getItem('lastInboxCheck') || new Date(Date.now() - 5 * 60 * 1000).toISOString();

      const response = await api.get(`/inbox/poll?since=${lastCheck}`);

      const newMessages = response.data?.new_messages || [];
      if (newMessages.length > 0) {
        toast.success(`${newMessages.length} new message(s)`);
        fetchConversations();

        // If viewing conversation, refresh messages
        if (selectedConversation) {
          fetchMessages(selectedConversation);
        }
      }

      if (response.data?.now) {
        localStorage.setItem('lastInboxCheck', response.data.now);
      }
    } catch (error) {
      console.error('Error polling messages:', error);
    }
  };

  const handleSendReply = async (channel) => {
    if (!replyText.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      setSending(true);

      await api.post(`/inbox/conversations/${selectedConversation}/reply`, {
        message: replyText,
        channel: channel
      });

      toast.success('Reply sent successfully');
      setReplyText('');

      // Refresh messages
      fetchMessages(selectedConversation);
    } catch (error) {
      console.error('Error sending reply:', error);
      toastSetupError(error.response?.data?.error, 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleSyncSms = async () => {
    try {
      setSyncing(true);
      const response = await api.post('/inbox/sync-sms', { days: 7 });
      toast.success(response.data.message || 'SMS sync complete');
      fetchConversations();
      fetchStats();
    } catch (error) {
      console.error('Error syncing SMS:', error);
      toast.error(error.response?.data?.error || 'Failed to sync SMS history');
    } finally {
      setSyncing(false);
    }
  };

  const handleMarkAsRead = async (buyerId, markRead = true) => {
    try {
      const endpoint = markRead ? 'mark-read' : 'mark-unread';
      await api.post(`/inbox/conversations/${buyerId}/${endpoint}`);

      fetchConversations();
    } catch (error) {
      console.error('Error updating read status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    try {
      if (target.type === 'conversation') {
        await inboxAPI.deleteConversation(target.id);
        toast.success('Conversation deleted');
        selectedRef.current = null;
        setSelectedConversation(null);
        setMessages([]);
        setEmails([]);
      } else {
        await inboxAPI.deleteMessage(target.id);
        toast.success('Message deleted');
        if (selectedConversation) {
          const response = await api.get(`/inbox/conversations/${selectedConversation}`);
          const remaining = response.data.messages || [];
          setMessages(remaining);
          if (remaining.length === 0) {
            selectedRef.current = null;
            setSelectedConversation(null);
          } else {
            fetchEmails(selectedConversation);
          }
        }
      }
      fetchConversations();
      fetchStats();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error.response?.data?.error || 'Failed to delete');
    }
  };

  const selectedConvData = conversations.find(c => c.buyer_id === selectedConversation);

  return (
    <div className="inbox-page">
      {/* Sidebar - Conversations List */}
      <div className="inbox-sidebar">
        {/* Header */}
        <div className="inbox-sidebar-header">
          <div className="inbox-header-row">
            <h1>Inbox</h1>
            <div className="inbox-header-actions">
              <button
                onClick={handleSyncSms}
                disabled={syncing}
                className="refresh-button"
                title="Pull SMS history from Twilio (last 7 days)"
              >
                {syncing ? <Loader2 className="w-5 h-5 spin" /> : <MessageSquare className="w-5 h-5" />}
              </button>
              <button
                onClick={() => {
                  fetchConversations();
                  pollNewMessages();
                }}
                className="refresh-button"
                title="Refresh inbox"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="inbox-stats">
              <StatCard label="Total" value={stats.total_conversations} />
              <StatCard label="Unread" value={stats.unread_conversations} highlight />
              <StatCard label="Today" value={stats.recent_24h} />
            </div>
          )}

          {/* Search */}
          <div className="inbox-search">
            <Search className="w-5 h-5" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>

          {/* Filters */}
          <div className="inbox-filters">
            <div className="filter-dropdown">
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="all">All</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
              </select>
            </div>
            <div className="filter-dropdown">
              <select
                value={filters.channel}
                onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
              >
                <option value="all">All Channels</option>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
            </div>
          </div>
        </div>

        {/* Conversations List */}
        <div className="conversations-list">
          {loading ? (
            <div className="conversations-loading">
              <Loader2 className="w-8 h-8 spin" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="conversations-empty">
              <Mail />
              <p>No conversations yet</p>
            </div>
          ) : (
            conversations.map((conversation) => (
              <ConversationCard
                key={conversation.buyer_id}
                conversation={conversation}
                isSelected={selectedConversation === conversation.buyer_id}
                onClick={() => fetchMessages(conversation.buyer_id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Main Area - Messages */}
      <div className="messages-area">
        {selectedConversation ? (
          <>
            {/* Conversation Header */}
            <div className="messages-header">
              <div className="messages-header-row">
                <div className="buyer-info">
                  <h2>
                    {selectedConvData?.business_name}
                  </h2>
                  <div className="buyer-details">
                    {selectedConvData?.email} • {selectedConvData?.campaign_name}
                  </div>
                </div>
                <div className="header-actions">
                  <button
                    onClick={() => setDeleteTarget({ type: 'conversation', id: selectedConversation })}
                    className="action-button delete-button"
                    title="Delete this conversation"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                  {selectedConvData?.unread_count > 0 ? (
                    <button
                      onClick={() => handleMarkAsRead(selectedConversation, true)}
                      className="action-button"
                    >
                      Mark as Read
                    </button>
                  ) : (
                    <button
                      onClick={() => handleMarkAsRead(selectedConversation, false)}
                      className="action-button"
                    >
                      Mark as Unread
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="messages-list">
              {messages.map((message) => {
                if (message.message_type === 'received' && message.contact_type === 'email') {
                  const email = emails.find((e) => e?.contact_history_id != null && String(e.contact_history_id) === String(message.id));
                  return (
                    <EmailReplyItem
                      key={message.id}
                      message={message}
                      email={email}
                      dncMarked={!!dncMarkedBuyers[selectedConversation]}
                      onMarkDnc={() => handleMarkDnc(selectedConversation)}
                      onDelete={() => setDeleteTarget({ type: 'message', id: message.id })}
                    />
                  );
                }
                return (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    onDelete={() => setDeleteTarget({ type: 'message', id: message.id })}
                  />
                );
              })}
            </div>

            {/* Reply Box */}
            <div className="reply-box">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply..."
                rows={3}
                className="reply-input"
              />
              <div className="reply-actions">
                <button
                  onClick={() => handleSendReply('sms')}
                  disabled={sending || !selectedConvData?.phone}
                  className="send-button send-sms"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 spin" />
                  ) : (
                    <MessageSquare className="w-4 h-4" />
                  )}
                  Send SMS
                </button>
                <button
                  onClick={() => handleSendReply('email')}
                  disabled={sending}
                  className="send-button send-email"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  Send Email
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="messages-empty">
            <div className="messages-empty-content">
              <Mail />
              <p>Select a conversation to view messages</p>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={deleteTarget?.type === 'conversation' ? 'Delete this conversation?' : 'Delete this message?'}
        message={deleteTarget?.type === 'conversation'
          ? 'The whole conversation is removed from your inbox. Your campaign history and statistics are not changed. It comes back only if this prospect replies again.'
          : 'This message is removed from your inbox. Your campaign history and statistics are not changed.'}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

const StatCard = ({ label, value, highlight }) => (
  <div className={`stat-card ${highlight ? 'highlight' : ''}`}>
    <div className="stat-card-value">
      {value}
    </div>
    <div className="stat-card-label">{label}</div>
  </div>
);

const ConversationCard = ({ conversation, isSelected, onClick }) => (
  <div
    onClick={onClick}
    className={`conversation-card ${isSelected ? 'selected' : ''}`}
  >
    <div className="conversation-header">
      <div className="conversation-info">
        <h3 className="conversation-name">
          {conversation.business_name}
        </h3>
      </div>
      {conversation.unread_count > 0 && (
        <div className="conversation-badges">
          <span className="badge unread">
            {conversation.unread_count}
          </span>
        </div>
      )}
    </div>
    <div className="conversation-preview">
      {conversation.last_message}
    </div>
    <div className="conversation-meta">
      <div className="flex items-center">
        {conversation.last_channel === 'email' ? (
          <Mail className="w-3 h-3 mr-1" />
        ) : (
          <MessageSquare className="w-3 h-3 mr-1" />
        )}
        {conversation.last_channel}
      </div>
      <span>{format(new Date(conversation.last_message_time), 'MMM d, h:mm a')}</span>
    </div>
  </div>
);

const MessageBubble = ({ message, onDelete }) => {
  const isSent = message.message_type === 'sent';
  const isEmail = message.contact_type === 'email';

  return (
    <div className={`message-item ${isSent ? 'sent' : 'received'}`}>
      <div className="message-bubble">
        <div className="message-meta">
          {isEmail ? (
            <Mail className="w-3 h-3" />
          ) : (
            <MessageSquare className="w-3 h-3" />
          )}
          <span>
            {format(new Date(isSent ? message.sent_at : message.response_timestamp), 'MMM d, h:mm a')}
          </span>
          {!isSent && !!message.is_read && (
            <CheckCheck className="w-3 h-3" />
          )}
          {onDelete && (
            <button type="button" className="message-delete" onClick={onDelete} title="Delete message" aria-label="Delete message">
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
        <p className="whitespace-pre-wrap">
          {isSent ? message.message_content : message.response_content}
        </p>
      </div>
    </div>
  );
};

export default InboxPage;
